import { useCallback, useMemo, useRef, useState } from 'react';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useNow } from '../hooks/useNow';
import { cancelShipBuild, scrapShips, type PlanetActionOptions } from '../services/planet.service';
import { getShipIcon } from '../utils/shipIcons';
import { formatCountdown, formatFullNumber } from '../utils/formatters';
import QuantityBar from './QuantityBar';
import ResourceQuantities from './ResourceQuantities';
import { Icon } from './ui/Icon';
import QueueItemCard from './QueueItemCard';
import { useHoldRepeat } from '../hooks/useHoldRepeat';
import { computeAutoUnlockSelection } from '../utils/autoUnlockSelection';
import { Panel } from './ui/Panel';
import { Button } from './ui/Button';
import { IconButton } from './ui/IconButton';
import { Chip } from './ui/Chip';
import { zoneAccentStyle } from './planet/zoneAccent';
import {
    BUILDING_NAMES,
    SHIP_BUILD_REFUND_MAX,
    SHIP_BUILD_REFUND_MIN,
    getShipDefinition,
    type Planet,
    type PlayerRace,
    type ResourceCost,
    type ShipId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateDockedPopulation,
    calculateNominalRefundAtRates,
    calculateShipBuildCancellationPopulation,
    calculateShipBuildCost,
    REFUND_RATES,
} from '@kaeliux/game-engine';

const REFUND_RANGE_LABEL = `${SHIP_BUILD_REFUND_MIN * 100}–${SHIP_BUILD_REFUND_MAX * 100}%`;

function sumRefund(a: ResourceCost, b: ResourceCost): ResourceCost {
    return {
        metal: (a.metal ?? 0) + (b.metal ?? 0),
        minerals: (a.minerals ?? 0) + (b.minerals ?? 0),
        gas: (a.gas ?? 0) + (b.gas ?? 0),
    };
}

/** Nominal (client-previewed) refund for scrapping `quantity` of `shipId` — the server computes the real credit. */
function refundForScrap(race: PlayerRace, shipId: ShipId, quantity: number): ResourceCost {
    return calculateNominalRefundAtRates(calculateShipBuildCost(race, shipId, quantity), REFUND_RATES.ship, 1);
}

interface ScrapShipRowProps {
    def: ReturnType<typeof getShipDefinition>;
    race: PlayerRace;
    docked: number;
    selected: number;
    disabled: boolean;
    onChange: (delta: number) => void;
    /** Absolute set, used by clicking directly on the bar (vs. the relative +/- steppers). */
    onSetQuantity: (quantity: number) => void;
}

/** One selectable docked ship type — a bar with −/+ ends, filled by how many of this type are selected to scrap. Clicking the bar jumps to that position; the ends support press-and-hold to auto-repeat (mirrors RecyclingPage's RecycleShipRow). */
function ScrapShipRow({ def, race, docked, selected, disabled, onChange, onSetQuantity }: ScrapShipRowProps) {
    const canIncrement = !disabled && selected < docked;
    const canDecrement = !disabled && selected > 0;
    const pct = docked > 0 ? Math.min(100, (selected / docked) * 100) : 0;

    const canIncrementRef = useRef(canIncrement);
    canIncrementRef.current = canIncrement;
    const canDecrementRef = useRef(canDecrement);
    canDecrementRef.current = canDecrement;

    const decrementHold = useHoldRepeat(useCallback(() => onChange(-1), [onChange]), canDecrementRef);
    const incrementHold = useHoldRepeat(useCallback(() => onChange(1), [onChange]), canIncrementRef);

    function setQuantityFromFraction(fraction: number) {
        if (docked <= 0) return;
        onSetQuantity(Math.round(fraction * docked));
    }

    return (
        <Panel variant="solid" padding="sm" className="flex items-center gap-3" data-testid="scrap-ship-row">
            <img
                src={getShipIcon(race, def.id)}
                alt=""
                className="w-16 aspect-[4/3] object-contain rounded-tile bg-space-950 border border-space-700 shrink-0"
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-content-primary font-semibold truncate">{def.name}</h3>
                    <span className="text-content-muted text-micro shrink-0">
                        Docked: {docked.toLocaleString()} · {def.hangar} pop each
                    </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <IconButton
                        variant="secondary"
                        size="lg"
                        className="text-lg leading-none select-none touch-none"
                        onClick={() => onChange(-1)}
                        {...decrementHold}
                        disabled={!canDecrement}
                        aria-label={`Scrap one fewer ${def.name}`}
                    >
                        −
                    </IconButton>
                    <QuantityBar
                        label={`${def.name} to scrap`}
                        value={selected}
                        max={docked}
                        pct={pct}
                        fillClassName="bg-status-danger/60"
                        disabled={disabled}
                        onSetFraction={setQuantityFromFraction}
                        onStep={onChange}
                    />
                    <IconButton
                        variant="secondary"
                        size="lg"
                        className="text-lg leading-none select-none touch-none"
                        onClick={() => onChange(1)}
                        {...incrementHold}
                        disabled={!canIncrement}
                        aria-label={`Scrap one more ${def.name}`}
                    >
                        +
                    </IconButton>
                </div>
                {selected > 0 && (
                    <p className="text-content-muted text-micro mt-1">
                        Frees {(def.hangar * selected).toLocaleString()} pop · Refund:{' '}
                        <ResourceQuantities amounts={refundForScrap(race, def.id, selected)} />
                    </p>
                )}
            </div>
        </Panel>
    );
}

interface QueuedBuildRowProps {
    planetId: string;
    race: PlayerRace;
    event: TickCheckpointEventOf<'shipBuild'>;
    now: number;
    options: PlanetActionOptions;
    onPlanetUpdated: (planet: Planet) => void;
}

/**
 * One pending ship batch, cancellable from the lock screen (issue #120).
 *
 * The footnote is the honest part: cancelling hands back the units the
 * shipyard already finished (and any Axon donors), so on a started batch it
 * pushes docked population UP — the opposite of what a player reaching for
 * "Cancel" on an overpopulation screen expects. What cancelling does buy is
 * that the batch never lands in full later and re-locks the planet.
 */
function QueuedBuildRow({ planetId, race, event, now, options, onPlanetUpdated }: QueuedBuildRowProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const def = getShipDefinition(race, event.eventDetails.shipType);
    const started = event.eventDetails.startDate.getTime() <= now;
    const deliverAt = event.eventDetails.deliverAt;
    // A Robots grouped batch (issue #91) sits pending past its own endDate
    // while delivery is held. The engine refuses to cancel one at that point,
    // so offering the button here would only ever produce a 412 — and the
    // countdown would run negative. Mirrors ShipyardPage's QueuedShipRow.
    const awaitingDelivery = !!deliverAt && now >= event.endDate.getTime();
    const remainingMs = awaitingDelivery ? deliverAt.getTime() - now : event.endDate.getTime() - now;
    const effect = calculateShipBuildCancellationPopulation(race, event, new Date(now));

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            onPlanetUpdated(await cancelShipBuild(planetId, event.eventId, options));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    function requestCancel() {
        if (awaitingDelivery) return;
        const consequence = effect.dockedAdded > 0
            ? `The ${effect.deliveredUnits.toLocaleString()} unit${effect.deliveredUnits === 1 ? '' : 's'} already finished ${effect.deliveredUnits === 1 ? 'is' : 'are'} delivered to your ${BUILDING_NAMES.hangars}, so this ADDS ${effect.dockedAdded.toLocaleString()} population right now and makes the lock deeper. It does stop the remaining ${formatFullNumber(effect.dockedOnCompletion)} population from landing when the batch would have completed.`
            : `Nothing has been built yet, so docked population is unchanged — but it stops ${formatFullNumber(effect.dockedOnCompletion)} population from landing when the batch would have completed.`;

        openDialog({
            title: `Cancel ${def.name} construction?`,
            message: `${consequence} The unbuilt remainder is refunded at ${REFUND_RANGE_LABEL} of its cost.`,
            confirmLabel: 'Cancel construction',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    return (
        <QueueItemCard
            image={
                <div className="w-full h-full rounded-tile border border-space-700 bg-space-950/60 flex items-center justify-center overflow-hidden">
                    <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                </div>
            }
            overlayLabel={`× ${event.eventDetails.quantity}`}
            name={def.name}
            statusText={
                awaitingDelivery
                    ? `Awaiting delivery — ${formatCountdown(remainingMs)}`
                    : `${started ? 'Building…' : 'Queued —'} ${formatCountdown(remainingMs)}`
            }
            footnote={
                awaitingDelivery
                    ? `Lands +${effect.dockedOnCompletion.toLocaleString()} pop on delivery — can no longer be cancelled`
                    : effect.dockedAdded > 0
                        ? `Cancel docks ${effect.deliveredUnits.toLocaleString()} now (+${effect.dockedAdded.toLocaleString()} pop)`
                        : `Lands +${effect.dockedOnCompletion.toLocaleString()} pop when done`
            }
            error={error}
            isCancelling={isCancelling}
            cancelDisabled={awaitingDelivery}
            onCancel={requestCancel}
            imageBoxClassName="w-20 aspect-[4/3]"
        />
    );
}

interface PlanetOverpopulationOverlayProps {
    planet: Planet;
    race: PlayerRace;
    onPlanetUpdated: (planet: Planet) => void;
}

/**
 * Replaces the routed content (in place of `<Outlet />`) when the player's
 * stationed ships occupy more population than the planet's Launcher can
 * support (issue #26). Distinct from `PlanetLockOverlay` (the Ozone-lock
 * screen) — the two locks are independent and AppShell renders both
 * together when a planet is caught by both at once.
 */
export default function PlanetOverpopulationOverlay({ planet, race, onPlanetUpdated }: PlanetOverpopulationOverlayProps) {
    const { resourceMultiplier, sunAges, speedMultiplier } = useUniverseStore();
    const [counts, setCounts] = useState<Partial<Record<ShipId, number>>>({});
    const [isScrapping, setIsScrapping] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const options: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier],
    );

    const now = useNow();

    const dockedRows = (Object.entries(planet.ships ?? {}) as [string, number][])
        .filter(([, count]) => count > 0)
        .map(([id, count]) => ({ def: getShipDefinition(race, Number(id) as ShipId), count }));

    const shipQueue = (planet.tickCheckpoints ?? []).filter(
        (e): e is TickCheckpointEventOf<'shipBuild'> => e.eventType === 'shipBuild' && !e.cancelled,
    );
    const incomingPopulation = shipQueue.reduce(
        (sum, e) => sum + getShipDefinition(race, e.eventDetails.shipType).hangar * e.eventDetails.quantity,
        0,
    );

    function adjust(shipId: ShipId, docked: number, delta: number) {
        setCounts((prev) => {
            const next = Math.max(0, Math.min(docked, (prev[shipId] ?? 0) + delta));
            return { ...prev, [shipId]: next };
        });
    }

    function setAbsoluteQuantity(shipId: ShipId, docked: number, quantity: number) {
        setCounts((prev) => ({ ...prev, [shipId]: Math.max(0, Math.min(docked, Math.round(quantity))) }));
    }

    const entries = (Object.entries(counts) as [string, number][]).filter(([, n]) => n > 0);
    const totalShips = entries.reduce((sum, [, n]) => sum + n, 0);
    const populationFreed = entries.reduce(
        (sum, [id, n]) => sum + getShipDefinition(race, Number(id) as ShipId).hangar * n,
        0,
    );
    const totalRefund = entries.reduce(
        (total, [id, n]) => sumRefund(total, refundForScrap(race, Number(id) as ShipId, n)),
        {} as ResourceCost,
    );

    const currentPopulation = calculateDockedPopulation(race, planet.ships);
    const capacity = planet.resources.population.capacity;
    const projectedPopulation = Math.max(0, currentPopulation - populationFreed);
    const deficit = Math.max(0, currentPopulation - capacity);

    function handleAutoUnlock() {
        setCounts(computeAutoUnlockSelection(race, planet.ships, deficit));
    }

    async function handleConfirm() {
        setIsScrapping(true);
        setError(null);
        try {
            const shipsToScrap = Object.fromEntries(
                entries.map(([id, n]) => [Number(id) as ShipId, n]),
            ) as Partial<Record<ShipId, number>>;
            const updated = await scrapShips(planet.id, shipsToScrap, options);
            onPlanetUpdated(updated);
            setCounts({});
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Scrapping failed');
        } finally {
            setIsScrapping(false);
        }
    }

    return (
        <div className="w-full flex justify-center px-4 py-8">
            <Panel
                variant="accent"
                padding="lg"
                style={zoneAccentStyle('military')}
                className="w-full max-w-2xl flex flex-col gap-6"
                data-testid="overpopulation-overlay"
            >
                <div className="flex flex-col gap-2 text-center">
                    <Icon name="population" size={36} className="text-status-danger mx-auto" aria-hidden="true" />
                    <h1 className="text-heading text-status-danger">Your planet is overpopulated.</h1>
                </div>

                <p className="text-content-secondary text-body text-center">
                    Your stationed ships occupy more population than your {BUILDING_NAMES.hangars} can support, and the planet is
                    locked until that&rsquo;s resolved. Scrap ships below to free up population — you&rsquo;ll
                    receive {Math.round(REFUND_RATES.ship * 100)}% of their build cost back in resources.
                </p>

                <Panel variant="solid" padding="md" className="flex flex-col gap-2">
                    <div className="flex justify-between">
                        <span className="text-content-muted">Population</span>
                        <Chip tone="danger">
                            {formatFullNumber(currentPopulation)} / {formatFullNumber(capacity)}
                        </Chip>
                    </div>
                    {totalShips > 0 && (
                        <div className="flex justify-between">
                            <span className="text-content-muted">After scrapping</span>
                            <span className={projectedPopulation <= capacity ? 'text-status-success font-semibold' : 'text-status-danger font-semibold'}>
                                {formatFullNumber(projectedPopulation)} / {formatFullNumber(capacity)}
                            </span>
                        </div>
                    )}
                </Panel>

                {dockedRows.length === 0 ? (
                    <p className="text-content-muted text-body">This planet has no docked ships to scrap.</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        <Button
                            variant="secondary"
                            size="sm"
                            className="self-start"
                            onClick={handleAutoUnlock}
                            disabled={isScrapping || deficit <= 0}
                            title="Select just enough ships to bring the planet back under its population capacity"
                        >
                            Auto-unlock
                        </Button>
                        <div className="flex flex-col gap-2">
                            {dockedRows.map(({ def, count }) => (
                                <ScrapShipRow
                                    key={def.id}
                                    def={def}
                                    race={race}
                                    docked={count}
                                    selected={counts[def.id] ?? 0}
                                    disabled={isScrapping}
                                    onChange={(delta) => adjust(def.id, count, delta)}
                                    onSetQuantity={(quantity) => setAbsoluteQuantity(def.id, count, quantity)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {shipQueue.length > 0 && (
                    <Panel variant="solid" padding="md" className="flex flex-col gap-3">
                        <div>
                            <h2 className="text-content-secondary font-semibold text-title">Ships still under construction</h2>
                            <p className="text-content-muted text-micro mt-1 leading-relaxed">
                                These batches don&rsquo;t count toward the lock yet, but they will dock when they
                                finish — another {formatFullNumber(incomingPopulation)} population heading for this
                                planet, enough to lock it again right after you scrap. Cancelling one keeps it from
                                landing. It won&rsquo;t unlock the planet on its own: any units the {BUILDING_NAMES.shipyard} has
                                already finished are delivered to your {BUILDING_NAMES.hangars} instead of refunded, so cancelling a
                                batch that has started raises docked population.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {shipQueue.map((event) => (
                                <QueuedBuildRow
                                    key={event.eventId}
                                    planetId={planet.id}
                                    race={race}
                                    event={event}
                                    now={now}
                                    options={options}
                                    onPlanetUpdated={onPlanetUpdated}
                                />
                            ))}
                        </div>
                    </Panel>
                )}

                {totalShips > 0 && (
                    <Panel variant="solid" padding="sm" className="flex justify-between">
                        <span className="text-content-muted text-body">Total refund ({totalShips} ship{totalShips === 1 ? '' : 's'})</span>
                        <span className="text-content-primary font-semibold text-body"><ResourceQuantities amounts={totalRefund} /></span>
                    </Panel>
                )}

                {error && <p className="text-status-danger text-body">{error}</p>}

                <div className="flex justify-end gap-3">
                    <Button
                        variant="danger"
                        onClick={handleConfirm}
                        disabled={totalShips === 0 || isScrapping}
                    >
                        {isScrapping ? 'Scrapping…' : `Scrap ${totalShips} ship${totalShips === 1 ? '' : 's'}`}
                    </Button>
                </div>
            </Panel>
        </div>
    );
}
